// A screen: filters that together pick areas (the Screener). Plain data and the URL form only, no DOM, so the
// view (web/screener.ts), the URL hash (web/main.ts) and saved screens share one format.
import type { Metric } from "./api.ts";

import { OUTLOOK, type Outlook } from "../shared/outlook.ts";
export type { Outlook };

export interface ScreenFilter {
  metric: string;
  op: "min" | "max";
  /** stored units (percents as fractions); null = not filled in yet, left out of the query */
  value: number | null;
}

/** One data point of a combined ranking; `dir` = which end is best. */
export interface ScreenRank {
  metric: string;
  dir: "high" | "low";
}

/** A combined ranking takes 2-3 data points (the API refuses 1). */
export const RANK_MAX = 3;

export interface Screen {
  filters: ScreenFilter[];
  /** rank the matches by these data points combined; fewer than 2 = not used yet */
  rank: ScreenRank[];
  outlook: Outlook | null;
  /** inside a state / metro / county, or within `miles` of any area's center */
  where: { mode: "in" | "near"; geo_id: string; name: string; miles: number } | null;
  sort: string | null;
  dir: "asc" | "desc" | null;
}

export const OUTLOOK_LABEL: Record<Outlook, string> = { up: OUTLOOK.up.label, unclear: OUTLOOK.unclear.label, down: OUTLOOK.down.label };

export const emptyScreen = (): Screen => ({ filters: [], rank: [], outlook: null, where: null, sort: null, dir: null });

/** The best end of a data point by default: its `higher:` (neutral counts higher as better, as growth usually reads). */
export const defaultRankDir = (m: Pick<Metric, "higher">): "high" | "low" => (m.higher === "bad" ? "low" : "high");

/** the ranking sent to the API: only once it has 2 data points */
export const activeRank = (s: Screen) => (s.rank.length >= 2 ? s.rank.slice(0, RANK_MAX) : []);

/** Starting points: a click fills the form. */
export const PRESETS: Array<{ label: string; screen: Partial<Screen> }> = [
  { label: "Within my budget", screen: { filters: [{ metric: "my_payment_pct", op: "max", value: 0.3 }] } },
  { label: "Affordable, leaning up", screen: { filters: [{ metric: "home_value", op: "max", value: 350_000 }], outlook: "up" } },
  { label: "Rent and prices rising", screen: { rank: [{ metric: "rent_yoy", dir: "high" }, { metric: "home_value_yoy", dir: "high" }] } },
  { label: "Buyer's markets", screen: { filters: [{ metric: "buyer_leverage", op: "min", value: 65 }] } },
  { label: "Low downside risk", screen: { filters: [{ metric: "decline_risk", op: "max", value: 0.08 }] } },
  { label: "Fairly valued & growing", screen: { filters: [{ metric: "overvalued_pct", op: "max", value: 0.05 }, { metric: "population_growth", op: "min", value: 0.01 }] } },
];

const pctUnit = (m: Pick<Metric, "unit">) => m.unit === "pct" || m.unit === "pct1";

/**
 * What the user typed → stored units. Percents are typed as percents ("6" or "6%" → 0.06); dollar amounts and counts
 * take k / m suffixes and commas ("350k", "$1.2m", "1,500"). null when empty or not a number.
 */
export function parseInput(m: Pick<Metric, "unit">, raw: string): number | null {
  const s = raw.trim().toLowerCase().replace(/[$,\s]/g, "").replace(/%$/, "").replace(/\/mo$/, "");
  const x = /^(-?(?:\d+\.?\d*|\.\d+))([km]?)$/.exec(s);
  if (!x) return null;
  const v = Number(x[1]) * (x[2] === "k" ? 1e3 : x[2] === "m" ? 1e6 : 1);
  return pctUnit(m) ? v / 100 : v;
}

/** Stored units → what goes back in the input box (the inverse of parseInput, without suffixes). */
export function inputValue(m: Pick<Metric, "unit">, v: number | null): string {
  if (v == null) return "";
  return String(Number((pctUnit(m) ? v * 100 : v).toPrecision(6)));
}

/** The unit hint next to the input box. */
export function unitHint(m: Pick<Metric, "unit">): string {
  switch (m.unit) {
    case "pct": case "pct1": return "%";
    case "usd": return "$";
    case "usd_month": return "$/mo";
    case "days": return "days";
    case "months": return "months";
    case "score": return "0-100";
    case "per_sqmi": return "/sq mi";
    default: return "";
  }
}

/** The screen as URL params: for /api/screen and, with `wn` (the place's name), for the app's URL and saved screens. */
export function screenParams(s: Screen, forApi = false): URLSearchParams {
  const q = new URLSearchParams();
  for (const f of s.filters) if (f.value != null && Number.isFinite(f.value)) q.append("f", `${f.metric}:${f.op}:${f.value}`);
  // the app's URL keeps a half-made ranking (1 data point) so a reload doesn't lose it; the API gets it from 2
  const rank = forApi ? activeRank(s) : s.rank;
  if (rank.length) q.set("rank", rank.map((r) => `${r.metric}:${r.dir}`).join(","));
  if (s.outlook) q.set("outlook", s.outlook);
  if (s.where) {
    q.set(s.where.mode, s.where.geo_id);
    if (s.where.mode === "near") q.set("miles", String(s.where.miles));
    if (!forApi) q.set("wn", s.where.name);
  }
  if (s.sort) q.set("sort", s.sort);
  if (s.dir) q.set("dir", s.dir);
  return q;
}

/** Read a screen back from URL params; null when the params hold no screen. Unknown metrics are dropped. */
export function parseScreen(q: URLSearchParams, isMetric: (id: string) => boolean): Screen | null {
  const filters: ScreenFilter[] = [];
  for (const f of q.getAll("f")) {
    const [metric = "", op = "", raw = ""] = f.split(":");
    const value = Number(raw);
    if (isMetric(metric) && (op === "min" || op === "max") && raw !== "" && Number.isFinite(value)) filters.push({ metric, op, value });
  }
  const rank: ScreenRank[] = [];
  for (const r of (q.get("rank") ?? "").split(",")) {
    const [metric = "", dir = ""] = r.split(":");
    if (isMetric(metric) && (dir === "high" || dir === "low") && !rank.some((x) => x.metric === metric) && rank.length < RANK_MAX) rank.push({ metric, dir });
  }
  const o = q.get("outlook");
  const outlook = o === "up" || o === "unclear" || o === "down" ? o : null;
  const geo = q.get("in") ?? q.get("near");
  const miles = Number(q.get("miles") ?? 25);
  const where = geo
    ? { mode: (q.has("in") ? "in" : "near") as "in" | "near", geo_id: geo, name: q.get("wn") ?? geo, miles: Number.isFinite(miles) && miles > 0 ? Math.min(miles, 500) : 25 }
    : null;
  if (!filters.length && !rank.length && !outlook && !where) return null;
  const d = q.get("dir");
  return { filters, rank, outlook, where, sort: q.get("sort"), dir: d === "asc" || d === "desc" ? d : null };
}

/**
 * One line for the map's chip and the saved list:
 * "Home Value ≤ $350,000 · Leaning up · within 25 mi of Austin · ranked by Rent Growth (YoY) + Home Value Growth (YoY)".
 */
export function describeScreen(s: Screen, metrics: Map<string, Metric>, fmt: (m: Metric, v: number) => string): string {
  const parts = s.filters.filter((f) => f.value != null).map((f) => {
    const m = metrics.get(f.metric);
    return m ? `${m.label} ${f.op === "min" ? "≥" : "≤"} ${fmt(m, f.value!)}` : f.metric;
  });
  if (s.outlook) parts.push(OUTLOOK_LABEL[s.outlook]);
  if (s.where) parts.push(s.where.mode === "in" ? `in ${s.where.name}` : `within ${s.where.miles} mi of ${s.where.name}`);
  const rank = activeRank(s);
  if (rank.length) {
    parts.push(`ranked by ${rank.map((r) => {
      const m = metrics.get(r.metric);
      if (!m) return r.metric;
      // say the direction only where it isn't the obvious one
      return r.dir === defaultRankDir(m) ? m.label : `${m.label} (${r.dir === "high" ? "highest" : "lowest"} best)`;
    }).join(" + ")}`);
  }
  return parts.join(" · ") || "No filters";
}
